/**
 * A cloud-only screen or control (the tenant administration, alerts, cloud productions, the vendor console, the offline
 * PIN) on a page whose primary is a studio server (container). Host-aware routing sends its calls straight to the cloud
 * (`connectivity/routing.ts`); only when that cannot work — no cloud, an offline session, the cloud not callable from
 * this page or down — does it show "needs a cloud connection" and why, instead of requests that would fail. Also the
 * node's "no studios on this site yet" state, which the rundown and production lists show.
 */
import type { ReactNode } from 'react';
import type { RouteId } from '@acp/api-contract/routes.js';
import { CloudPortalNotice } from '../components/CloudPortalNotice.js';
import { useNodeSiteWithoutStudios, useRouteAvailability, useServices } from '../app/hooks.js';
import { unavailableText } from '../connectivity/routing.js';
import { portalLink } from '../lib/hosts.js';

export type CloudOnlyGateProps = {
  /** The route the screen reads first: whether it can be sent decides. */
  readonly route: RouteId;
  /** What it is (`Alerts`), for the title. */
  readonly what: string;
  /** The same screen's path in the cloud portal (`/alerts`), linked when this page cannot call the cloud. */
  readonly path: string;
  /** A control inside a page rather than a page of its own: a narrower notice. */
  readonly inline?: boolean;
  readonly children: ReactNode;
};

export function CloudOnlyGate({ route, what, path, inline, children }: CloudOnlyGateProps) {
  const services = useServices();
  const target = useRouteAvailability(route);
  if (target.ok) return children;
  return (
    <CloudPortalNotice
      testId="needs-cloud"
      reason={target.reason}
      title={`${what} needs a cloud connection`}
      detail={unavailableText(target.reason)}
      href={target.reason === 'cloud-not-callable' ? portalLink(services.portalUrl, path) : null}
      linkLabel={`Open ${what} in the cloud portal`}
      {...(inline === true ? { inline: true } : {})}
    />
  );
}

/** The node's lists are empty because its site has no studio: say so, and where to fix it. Null anywhere else. */
export function NodeSiteWithoutStudios({ what }: { readonly what: 'rundowns' | 'productions' }) {
  const services = useServices();
  const empty = useNodeSiteWithoutStudios();
  if (empty === null) return null;
  const site = empty.siteId === null ? 'this server’s site' : `site ${empty.siteId}`;
  return (
    <CloudPortalNotice
      testId="node-no-studios"
      reason={null}
      title="This server’s site has no studios yet"
      detail={`A studio server holds only its own site’s studios and the ${what} that run in them. Assign a studio to ${site} in the cloud portal (Studios → a studio → Site), then its rundowns and productions appear here.`}
      href={portalLink(services.portalUrl, '/studios')}
      linkLabel="Open Studios in the cloud portal"
      inline
    />
  );
}
